import { createFileRoute } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'
import * as s from '@/components/legal-styles'

export const Route = createFileRoute('/legal/subprocessors')({
  component: SubprocessorsPage,
})

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: 14, lineHeight: 1.7,
}
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px', borderBottom: '2px solid #EDE9E4',
  fontWeight: 700, color: '#1a1216', fontSize: 13,
}
const tdStyle: React.CSSProperties = {
  padding: '10px 14px', borderBottom: '1px solid #EDE9E4', color: '#4a4745', fontSize: 14,
}

function SubprocessorsPage() {
  return (
    <div>
      <Helmet>
        <title>Sub-processors | One-Email</title>
        <meta name="description" content="One-Email sub-processor list. Third-party companies that process data on our behalf, with 30-day advance notification for changes." />
        <link rel="canonical" href="https://one-email.com/legal/subprocessors" />
      </Helmet>

      <h1 style={s.heading}>Sub-processors</h1>
      <p style={s.updated}>Last updated: March 24, 2026</p>

      <div style={s.highlight}>
        <strong>Summary:</strong> These are the third-party companies that process data on our behalf to deliver the One-Email service. We notify you at least 30 days before adding a new sub-processor.
      </div>

      {/* Current Sub-processors */}
      <h2 id="current" style={s.h2}>Current Sub-processors</h2>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Sub-processor</th>
              <th style={thStyle}>Location</th>
              <th style={thStyle}>Services Used</th>
              <th style={thStyle}>Data Processed</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}><strong>Cloudflare</strong></td>
              <td style={tdStyle}>Global (US-headquartered)</td>
              <td style={tdStyle}>Workers, R2, KV, Queues, Durable Objects, Hyperdrive, Pages</td>
              <td style={tdStyle}>API requests, email payloads (in transit via Queue), templates (R2), cached auth/suppression data (KV), dashboard/marketing site hosting</td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Neon</strong></td>
              <td style={tdStyle}>EU region</td>
              <td style={tdStyle}>Managed serverless Postgres</td>
              <td style={tdStyle}>All persistent data: accounts, email metadata, DKIM keys (encrypted), domains, API key hashes, suppression lists, enforcement logs</td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Scaleway</strong></td>
              <td style={tdStyle}>Paris, France (EU)</td>
              <td style={tdStyle}>Dedicated servers</td>
              <td style={tdStyle}>Email content (in transit for SMTP delivery), SMTP delivery logs, queue files (encrypted at rest)</td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Polar</strong></td>
              <td style={tdStyle}>Per <a href="https://polar.sh/privacy" target="_blank" rel="noopener noreferrer">Polar&apos;s privacy policy</a></td>
              <td style={tdStyle}>Payment/subscription processing</td>
              <td style={tdStyle}>Billing data: name, email, subscription details. One-Email does NOT store credit cards or payment tokens.</td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Vercel</strong></td>
              <td style={tdStyle}>US (Vercel API)</td>
              <td style={tdStyle}>DNS record management (auto-configure flow only)</td>
              <td style={tdStyle}>Encrypted OAuth access token (stored in Neon), domain DNS records. <strong>Used only when you explicitly authorize via OAuth popup.</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Explicitly NOT Used */}
      <h2 id="not-used" style={s.h2}>Explicitly NOT Used</h2>
      <p style={s.p}>The following are <strong>not</strong> sub-processors of One-Email:</p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Amazon Web Services (AWS/SES)</strong> &mdash; We do not use AWS SES or any AWS service. We do not fall back to any third-party email relay. All email is sent through our own SMTP infrastructure on Scaleway.</li>
        <li style={s.li}><strong>Hetzner</strong> &mdash; Not used in production. Hetzner was used for early benchmarks only. Production SMTP runs on Scaleway in Paris.</li>
        <li style={s.li}><strong>Google Analytics, Mixpanel, Segment</strong> &mdash; We use no third-party analytics services.</li>
        <li style={s.li}><strong>Any advertising network or SDK</strong> &mdash; We use no ad tech.</li>
      </ul>

      {/* Data Protection Agreements */}
      <h2 id="agreements" style={s.h2}>Data Protection Agreements</h2>
      <p style={s.p}>
        Each sub-processor is bound by a data processing agreement that imposes data protection obligations no less protective than those in our <a href="/legal/dpa">DPA</a>. Where sub-processors are located outside the EU/EEA, appropriate transfer mechanisms are in place (Standard Contractual Clauses, EU-US Data Privacy Framework, or both).
      </p>

      {/* Changes to Sub-processors */}
      <h2 id="changes" style={s.h2}>Changes to Sub-processors</h2>
      <p style={s.p}>When we add a new sub-processor:</p>
      <ol style={{ ...s.ul, listStyleType: 'decimal' }}>
        <li style={s.li}>We will <strong>notify you at least 30 days in advance</strong> via email to your account&apos;s registered address and via notice on this page.</li>
        <li style={s.li}>You have <strong>30 days to object</strong>. If your objection cannot be reasonably resolved, you may terminate the Agreement per the <a href="/legal/dpa">DPA</a>.</li>
      </ol>

      <h3 style={s.h3}>Subscribe to Updates</h3>
      <p style={s.p}>
        To receive email notifications when this list changes, enter your email below.
      </p>
      <p style={{ ...s.p, fontStyle: 'italic', color: '#999' }}>
        [Email subscription form &mdash; coming soon]
      </p>
      <p style={s.p}>
        Alternatively, you can monitor this page directly &mdash; we will update the &ldquo;Last updated&rdquo; date whenever a change is made.
      </p>

      {/* Archive */}
      <h2 id="archive" style={s.h2}>Archive</h2>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Change</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>March 24, 2026</td>
              <td style={tdStyle}>Initial publication</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
